(() => {
  const ready = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  };

  const optionsFor = (kind) => {
    if (kind === "collections") {
      return {
        cellAlign: "left",
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        dragThreshold: 8,
        groupCells: 5,
        freeScroll: false,
      };
    }
    if (kind === "testimonials") {
      // One quote per page, height follows the active quote
      return {
        cellAlign: "center",
        contain: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        adaptiveHeight: true,
        dragThreshold: 8,
        groupCells: 1,
        freeScroll: false,
      };
    }
    if (kind === "hero") {
      return {
        cellAlign: "left",
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        dragThreshold: 8,
        groupCells: 1,
        freeScroll: false,
        adaptiveHeight: false,
        pauseAutoPlayOnHover: true,
      };
    }
    if (kind === "products-compact") {
      return {
        cellAlign: "left",
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        dragThreshold: 8,
        groupCells: 3,
        freeScroll: false,
      };
    }
    // Full-width product carousels: 5 per page
    return {
      cellAlign: "left",
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: true,
      dragThreshold: 8,
      groupCells: 5,
      freeScroll: false,
    };
  };

  const initFlickity = (root) => {
    if (typeof Flickity === "undefined") return null;
    const viewport = root.querySelector("[data-para-flickity-viewport]");
    if (!viewport || viewport.flickityInstalled) return null;

    const kind = root.getAttribute("data-para-flickity") || "products";
    const options = optionsFor(kind);

    const autoplayMs = parseInt(root.getAttribute("data-para-autoplay") || "0", 10);
    if (autoplayMs > 0) options.autoPlay = autoplayMs;

    const flkty = new Flickity(viewport, options);
    viewport.flickityInstalled = true;

    const prev = root.querySelector("[data-para-prev]");
    const next = root.querySelector("[data-para-next]");
    prev?.addEventListener("click", () => flkty.previous());
    next?.addEventListener("click", () => flkty.next());

    const bars = [...root.querySelectorAll("[data-para-bar]")];
    if (bars.length) {
      const syncBars = () => {
        const idx = flkty.selectedIndex;
        bars.forEach((b, i) => {
          const on = i === idx;
          b.classList.toggle("is-active", on);
          b.setAttribute("aria-selected", on ? "true" : "false");
        });
      };
      bars.forEach((b, i) => {
        b.addEventListener("click", (e) => {
          e.preventDefault();
          flkty.select(i);
        });
      });
      flkty.on("change", syncBars);
      flkty.on("settle", syncBars);
      syncBars();
    }

    // Testimonials: swap the portrait with the active slide
    const avatars = [...root.querySelectorAll("[data-para-avatar]")];
    if (avatars.length) {
      const syncAvatars = () => {
        avatars.forEach((a, i) => a.classList.toggle("is-active", i === flkty.selectedIndex));
      };
      flkty.on("select", syncAvatars);
      syncAvatars();
    }

    return flkty;
  };

  /*
    Mega menu hover intent.
    Horizon closes a menu the moment the pointer leaves the link's <li>. The panel opens below the
    header padding, so a normal diagonal mouse move can cross a dead gap and close the menu (and
    it then looks stuck). Keep the menu open while the pointer is anywhere in the zone between the
    link and the panel, and only close after it has stayed outside for a short moment.
  */
  const initMegaMenuIntent = () => {
    if (document.documentElement.dataset.paraIntent) return;
    document.documentElement.dataset.paraIntent = "true";

    // Looked up on demand: Horizon re-renders the header after load, which replaces the element
    const getMenu = () => document.querySelector("header-menu");

    const pointer = { x: 0, y: 0 };
    let pendingItem = null;
    let closeTimer;

    const zoneContains = (x, y) => {
      const menu = getMenu();
      const link = menu && menu.querySelector('.menu-list__link[aria-expanded="true"]');
      const header = menu && menu.closest("header-component");
      const item = link && link.closest(".menu-list__list-item");
      if (!item || !header) return false;
      const li = item.getBoundingClientRect();
      const headerBottom = header.getBoundingClientRect().bottom;
      const panelHeight = parseFloat(header.style.getPropertyValue("--submenu-height")) || 0;
      // The link itself plus the band below it, down to the header edge
      const inLinkColumn = x >= li.left - 8 && x <= li.right + 8 && y >= li.top && y <= headerBottom + 4;
      const inGapBand = y >= li.bottom - 1 && y <= headerBottom + 4;
      // The opened panel
      const inPanel = y >= headerBottom - 4 && y <= headerBottom + panelHeight;
      return inLinkColumn || inGapBand || inPanel;
    };

    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        closeTimer = undefined;
        const menu = getMenu();
        if (!menu || !pendingItem || zoneContains(pointer.x, pointer.y)) return;
        const item = pendingItem;
        pendingItem = null;
        menu.deactivate({ target: item, relatedTarget: null, type: "pointerleave" });
      }, 160);
    };

    document.addEventListener(
      "pointermove",
      (e) => {
        pointer.x = e.clientX;
        pointer.y = e.clientY;
        if (!pendingItem) return;
        if (zoneContains(pointer.x, pointer.y)) {
          clearTimeout(closeTimer);
          closeTimer = undefined;
        } else if (!closeTimer) {
          scheduleClose();
        }
      },
      { passive: true }
    );

    // Capture phase on document: runs before Horizon's own pointerleave handler on the <li>
    document.addEventListener(
      "pointerleave",
      (e) => {
        const item = e.target;
        if (!(item instanceof Element) || !item.matches("header-menu .menu-list__list-item")) return;
        e.stopImmediatePropagation();
        pointer.x = e.clientX;
        pointer.y = e.clientY;
        pendingItem = item;
        if (zoneContains(pointer.x, pointer.y)) {
          clearTimeout(closeTimer);
          closeTimer = undefined;
        } else {
          scheduleClose();
        }
      },
      true
    );
  };

  ready(() => {
    document.body.classList.add("para-theme");
    initMegaMenuIntent();

    // Desktop "Masquer/Afficher les filtres" toggle for the vertical collection sidebar.
    // Delegated on document since filter changes can re-render the facets markup.
    document.addEventListener("click", (e) => {
      const button = e.target.closest("[data-para-facets-toggle]");
      if (!button) return;
      const wrapper = document.querySelector(".facets-block-wrapper--vertical");
      if (!wrapper) return;
      const nowHidden = wrapper.classList.toggle("hidden");
      button.setAttribute("aria-expanded", String(!nowHidden));
      const label = button.querySelector("[data-para-facets-toggle-label]");
      if (label) label.textContent = nowHidden ? "Afficher les filtres" : "Masquer les filtres";
    });

    /*
      Sections with data-para-filter-url load their products from a filtered collection page
      (e.g. /collections/loreal-paris?filter.p.product_type=Cheveux), since Liquid cannot filter
      a collection by product type. The products the section rendered on the server are the
      fallback if the request fails.
    */
    const hydrateFiltered = async (root) => {
      const url = root.getAttribute("data-para-filter-url");
      const limit = parseInt(root.getAttribute("data-para-filter-limit") || "9", 10);
      const viewport = root.querySelector("[data-para-flickity-viewport]");
      if (!url || !viewport) return;
      try {
        const res = await fetch(url, { headers: { "X-Requested-With": "fetch" } });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        const cards = [...doc.querySelectorAll("product-card.para-product-card")].slice(0, limit);
        if (!cards.length) return;
        viewport.replaceChildren(
          ...cards.map((card) => {
            const node = document.importNode(card, true);
            node.classList.add("carousel-cell");
            return node;
          })
        );
      } catch (e) {
        /* keep the server-rendered fallback */
      }
    };

    document.querySelectorAll("[data-para-flickity]").forEach(async (root) => {
      if (root.hasAttribute("data-para-filter-url")) {
        await hydrateFiltered(root);
        root.removeAttribute("data-para-loading");
      }
      initFlickity(root);
    });
  });
})();
